import { createSlice } from '@reduxjs/toolkit';
import initialState, { emptyForm } from './initialState';
import {
  getEventTypes,
  getEventType,
  createEventType,
  deleteEventType,
  deleteEventTypes,
  updateEventType,
} from './services';
import mapEventTypeToForm from './mapEventTypeToForm';
import getSelectableFilteredIds from './utils';
import {
  CONTACT_NOTIFICATION_BY_OPTIONS,
  createDefaultClosures,
  getFirstFreeContactNotificationOption,
  normalizeClosures,
  normalizeContactNotificationItem,
} from './constants';
import {
  clearFormErrorEntry,
  createEmptyFormErrors,
  FORM_ERROR_SECTIONS,
  reindexIndexedSectionErrors,
} from './formErrors';
import {
  CLOSURE_ERROR,
  getClosureValidationError,
  getDuplicateNameError,
} from '@/components/EventTypeForm/FinalizationReport/utils/validateClosures';
import {
  getDuplicateEventTypeNameError,
  getNameValidationError,
} from '@/components/EventTypeForm/utils/validateEventTypeForm';
import { isCompleteColor } from '@/components/EventTypes/utils/getCSSBackgroundColor';

export const EventTypesSlice = createSlice({
  name: 'EventTypes',
  initialState,
  reducers: {
    toggleSelectedId(state, action) {
      const targetId = action.payload;

      if (state.selectedIds.includes(targetId)) {
        state.selectedIds = state.selectedIds.filter((id) => id !== targetId);
      } else {
        state.selectedIds = [...state.selectedIds, targetId];
      }
    },
    setFilterText(state, action) {
      state.filterText = action.payload;
    },
    setFormField(state, action) {
      const { field, value } = action.payload;

      if (
        field === 'requiredReport' &&
        state.form.isSelfScheduled &&
        state.form.id
      ) {
        return;
      }

      state.form[field] = value;

      if (field === 'color' && isCompleteColor(value)) {
        state.form.lastValidColor = value;
      }

      if (field === 'name') {
        if (!value?.trim()) {
          state.form.errors.name = null;
        } else {
          state.form.errors.name = getDuplicateEventTypeNameError(
            value,
            [...state.generalEvents, ...state.visitEvents],
            state.form.id
          );
        }
      }

      if (field === 'isVisit' && value === true) {
        const rf = state.form.requiredFields;
        rf.users = Math.max(1, rf.users);
        rf.contacts = Math.max(1, rf.contacts);
        rf.properties = Math.max(1, rf.properties);
      }
    },
    setRequiredField(state, action) {
      if (state.form.isSelfScheduled && state.form.id) return;

      const { field, value } = action.payload;
      const min = state.form.isVisit ? 1 : 0;
      state.form.requiredFields[field] = Math.max(min, value);

      if (field === 'contacts' && state.form.requiredFields.contacts === 0) {
        state.form.notifyContacts.enabled = false;
      }
    },
    setNotifyField(state, action) {
      const { target, field, value } = action.payload;
      state.form[target][field] = value;
    },
    setContactNotificationField(state, action) {
      const { index, field, value } = action.payload;
      state.form.notifyContacts.items[index][field] = value;
    },
    addContactNotification(state) {
      const { items } = state.form.notifyContacts;

      if (items.length >= CONTACT_NOTIFICATION_BY_OPTIONS.length) return;

      state.form.notifyContacts.items.push({
        id: `contact-${Date.now()}`,
        value: 60,
        unit: 'minutes',
        by: getFirstFreeContactNotificationOption(items),
      });
    },
    removeContactNotification(state, action) {
      if (state.form.notifyContacts.items.length <= 1) return;
      state.form.notifyContacts.items.splice(action.payload, 1);
    },
    setClosure(state, action) {
      const { index, field, value } = action.payload;
      const closure = state.form.closures[index];

      if (!closure || (field === 'classification' && closure.locked)) return;

      state.form.closures[index][field] = value;

      const closureError =
        state.form.errors[FORM_ERROR_SECTIONS.CLOSURES][index];

      if (field === 'name' && closureError === CLOSURE_ERROR.DUPLICATE_NAME) {
        delete state.form.errors[FORM_ERROR_SECTIONS.CLOSURES][index];
      } else if (closureError) {
        const updatedClosure = state.form.closures[index];
        const isDuplicateError = closureError === CLOSURE_ERROR.DUPLICATE_NAME;
        const error = getClosureValidationError(updatedClosure, {
          closures: state.form.closures,
          index,
          checkDuplicate: isDuplicateError,
        });

        if (!error) {
          delete state.form.errors[FORM_ERROR_SECTIONS.CLOSURES][index];
        } else {
          state.form.errors[FORM_ERROR_SECTIONS.CLOSURES][index] = error;
        }
      }
    },
    validateEventTypeNameOnBlur(state) {
      state.form.errors.name = getNameValidationError(state.form.name, {
        events: [...state.generalEvents, ...state.visitEvents],
        currentId: state.form.id,
      });
    },
    validateClosureNameOnBlur(state, action) {
      const { index } = action.payload;
      const duplicateError = getDuplicateNameError(state.form.closures, index);

      if (duplicateError) {
        state.form.errors[FORM_ERROR_SECTIONS.CLOSURES][index] = duplicateError;
        return;
      }

      if (
        state.form.errors[FORM_ERROR_SECTIONS.CLOSURES][index] ===
        CLOSURE_ERROR.DUPLICATE_NAME
      ) {
        delete state.form.errors[FORM_ERROR_SECTIONS.CLOSURES][index];
      }
    },
    setFormErrors(state, action) {
      state.form.errors = action.payload;
    },
    resetFormErrors(state) {
      state.form.errors = createEmptyFormErrors();
    },
    clearFormError(state, action) {
      state.form.errors = clearFormErrorEntry(
        state.form.errors,
        action.payload
      );
    },
    addClosure(state) {
      state.form.closures.push({
        id: `closure-${Date.now()}`,
        classification: null,
        name: '',
      });
    },
    removeClosure(state, action) {
      const removedIndex = action.payload;
      const closure = state.form.closures[removedIndex];

      if (!closure || closure.locked) return;

      if (state.form.id && typeof closure.id === 'number') {
        state.form.deletedClosureIds.push(closure.id);
      }

      state.form.closures.splice(removedIndex, 1);
      state.form.errors[FORM_ERROR_SECTIONS.CLOSURES] =
        reindexIndexedSectionErrors(
          state.form.errors[FORM_ERROR_SECTIONS.CLOSURES],
          removedIndex
        );
    },
    resetForm(state) {
      state.form = {
        ...emptyForm,
        requiredFields: { ...emptyForm.requiredFields },
        notifyUsers: { ...emptyForm.notifyUsers },
        notifyContacts: {
          ...emptyForm.notifyContacts,
          items: emptyForm.notifyContacts.items.map((item) => ({ ...item })),
        },
        closures: createDefaultClosures(),
        deletedClosureIds: [],
        errors: createEmptyFormErrors(),
        loading: false,
        saving: false,
        error: null,
        pendingRequestId: null,
      };
    },
    resetListState(state) {
      state.loading = true;
      state.selectedIds = [];
      state.filterText = '';
    },
    toggleSelectAll(state) {
      const filteredIds = getSelectableFilteredIds(
        state.generalEvents,
        state.visitEvents,
        state.filterText
      );

      const allFilteredSelected = filteredIds.every((id) =>
        state.selectedIds.includes(id)
      );

      if (allFilteredSelected) {
        state.selectedIds = state.selectedIds.filter(
          (id) => !filteredIds.includes(id)
        );
      } else {
        const merged = new Set([...state.selectedIds, ...filteredIds]);
        state.selectedIds = Array.from(merged);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEventTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEventTypes.fulfilled, (state, action) => {
        const events = action.payload?.event_types ?? [];
        state.visitEvents =
          action.payload?.visit_events ?? events.filter((e) => e.is_visit);
        state.generalEvents =
          action.payload?.general ?? events.filter((e) => !e.is_visit);
        state.loading = false;

        if (state.form.name?.trim()) {
          state.form.errors.name = getDuplicateEventTypeNameError(
            state.form.name,
            [...state.generalEvents, ...state.visitEvents],
            state.form.id
          );
        }
      })
      .addCase(getEventTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getEventType.pending, (state, action) => {
        state.form.loading = true;
        state.form.error = null;
        state.form.pendingRequestId = action.meta.requestId;
      })
      .addCase(getEventType.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.form.pendingRequestId) return;
        const mapped = mapEventTypeToForm(action.payload);
        state.form = {
          ...emptyForm,
          ...mapped,
          requiredFields: {
            ...emptyForm.requiredFields,
            ...mapped.requiredFields,
          },
          notifyUsers: { ...emptyForm.notifyUsers, ...mapped.notifyUsers },
          notifyContacts: {
            enabled: mapped.notifyContacts.enabled,
            items: mapped.notifyContacts.items.map((item) =>
              normalizeContactNotificationItem(item)
            ),
          },
          closures: normalizeClosures(mapped.closures),
          deletedClosureIds: [],
          errors: createEmptyFormErrors(),
          loading: false,
          saving: false,
          error: null,
          pendingRequestId: null,
        };
      })
      .addCase(getEventType.rejected, (state, action) => {
        state.form.loading = false;
        state.form.error = action.error.message;
      })
      .addCase(deleteEventTypes.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteEventTypes.fulfilled, (state, action) => {
        const idsToRemove = action.meta.arg;
        state.generalEvents = state.generalEvents.filter(
          (event) => !idsToRemove.includes(event.id)
        );
        state.visitEvents = state.visitEvents.filter(
          (event) => !idsToRemove.includes(event.id)
        );
        state.selectedIds = state.selectedIds.filter(
          (id) => !idsToRemove.includes(id)
        );
        state.deleting = false;
      })
      .addCase(deleteEventTypes.rejected, (state) => {
        state.deleting = false;
      })
      .addCase(deleteEventType.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteEventType.fulfilled, (state, action) => {
        const idToRemove = action.meta.arg;
        state.generalEvents = state.generalEvents.filter(
          (event) => event.id !== idToRemove
        );
        state.visitEvents = state.visitEvents.filter(
          (event) => event.id !== idToRemove
        );
        state.selectedIds = state.selectedIds.filter((id) => id !== idToRemove);
        state.deleting = false;
      })
      .addCase(deleteEventType.rejected, (state) => {
        state.deleting = false;
      })
      .addCase(createEventType.pending, (state) => {
        state.form.saving = true;
        state.form.error = null;
      })
      .addCase(createEventType.fulfilled, (state) => {
        state.form.saving = false;
      })
      .addCase(createEventType.rejected, (state, action) => {
        state.form.saving = false;
        state.form.error = action.error.message;
      })
      .addCase(updateEventType.pending, (state) => {
        state.form.saving = true;
        state.form.error = null;
      })
      .addCase(updateEventType.fulfilled, (state) => {
        state.form.saving = false;
      })
      .addCase(updateEventType.rejected, (state, action) => {
        state.form.saving = false;
        state.form.error = action.error.message;
      });
  },
});

export const {
  toggleSelectedId,
  toggleSelectAll,
  setFilterText,
  setFormField,
  setRequiredField,
  setNotifyField,
  setContactNotificationField,
  addContactNotification,
  removeContactNotification,
  setClosure,
  validateClosureNameOnBlur,
  validateEventTypeNameOnBlur,
  setFormErrors,
  resetFormErrors,
  clearFormError,
  addClosure,
  removeClosure,
  resetForm,
  resetListState,
} = EventTypesSlice.actions;

export const selectIsSelfScheduledEdit = (state) =>
  Boolean(
    state.EventTypesSlice.form.id && state.EventTypesSlice.form.isSelfScheduled
  );

export default EventTypesSlice.reducer;
